import { useState, useEffect } from "react";
import Calendar from "react-calendar";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import LinearProgress from "@mui/material/LinearProgress";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SendIcon from "@mui/icons-material/Send";

// My Files
import firebase from "../../../firebase";
import SuccessDialog from "../../SuccessDialog";
import { jsonFromArray } from "../../../utility";
import { sendSharedItemEmails } from "../../shareDialogDelivery";
import "../../calendar/Calendar.css";

function NotificationMethodRow({
    icon,
    title,
    description,
    checked,
    onChange,
    name,
    divided = false,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                borderTop: divided ? "1px solid" : "none",
                borderColor: "divider",
            }}
        >
            <Box sx={{ color: "text.secondary", display: "grid" }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Switch
                checked={checked}
                onChange={onChange}
                name={name}
                inputProps={{ "aria-label": title }}
            />
        </Box>
    );
}

export default function EditFileNotificationsDialog(props) {
    const { enqueueSnackbar } = useSnackbar();

    const [sendNotifications, setSendNotifications] = useState(false);
    const [sendEmails, setSendEmails] = useState(false);
    const [embargoNotifications, setEmbargoNotifications] = useState(false);
    const [message, setMessage] = useState("");
    const [fromDate, setFromDate] = useState(new Date());
    const [isSending, setIsSending] = useState(false);
    const [confirmation, setConfirmation] = useState(null);

    useEffect(() => {
        if (props.open) {
            setSendNotifications(false);
            setSendEmails(false);
            setEmbargoNotifications(false);
            setMessage("");
            setFromDate(new Date());
            setIsSending(false);
            setConfirmation(null);
        }
    }, [props.open]);

    const handleClose = () => {
        if (!isSending) {
            props.onClose();
        }
    };

    const handleConfirmationClose = () => {
        setConfirmation(null);
        props.onClose();
    };

    const handleChangeMessageText = (event) => {
        setMessage(event.target.value);
    };

    function handleSwitchChange(event) {
        if (event.target.name === "send") {
            setSendNotifications(event.target.checked);
        } else if (event.target.name === "email") {
            setSendEmails(event.target.checked);
        } else if (event.target.name === "embargo") {
            setFromDate(new Date());
            setEmbargoNotifications(event.target.checked);
        }
    }

    function onFromDateChange(nextValue) {
        setFromDate(nextValue);
    }

    async function handleSendClick() {
        let feedbackParts = [];
        let sendTasks = [];

        if (sendNotifications) {
            sendTasks.push(sendInPlatformNotifications());
            feedbackParts.push("Notifications sent");
        }

        if (sendEmails) {
            const isEmbargoed = dayjs(fromDate).isAfter(dayjs(), "day");
            sendTasks.push(sendFileEmails());

            if (isEmbargoed) {
                feedbackParts.push(
                    "Emails scheduled for " +
                        dayjs(fromDate).format("DD/MM/YYYY"),
                );
            } else {
                feedbackParts.push("Emails sent");
            }
        }

        if (feedbackParts.length === 0) {
            return;
        }

        setIsSending(true);

        try {
            await Promise.all(sendTasks);
            setIsSending(false);
            // The dialog stays open behind the confirmation and closes with it
            setConfirmation({
                title:
                    !sendNotifications &&
                    dayjs(fromDate).isAfter(dayjs(), "day")
                        ? "Emails scheduled"
                        : "Notifications sent",
                details: feedbackParts,
            });
        } catch (error) {
            console.error("Unable to send file notifications", error);
            enqueueSnackbar(
                "Some notifications could not be sent. Please try again.",
                {
                    variant: "error",
                    autoHideDuration: 5000,
                },
            );
            setIsSending(false);
        }
    }

    function sendInPlatformNotifications() {
        var notificationsRef = firebase.database().ref("notifications");

        var fileType = "file";

        if (props.file.type.includes("video")) {
            fileType = "video";
        } else if (props.file.type === "application/pdf") {
            fileType = "pdf";
        } else if (props.file.type.includes("audio")) {
            fileType = "audio";
        } else if (props.file.type.includes("image")) {
            fileType = "image";
        } else if (props.file.type === "link") {
            fileType = "link";
        }

        let managersObject = {};
        props.managers.forEach((manager) => {
            managersObject[manager.key] = true;
        });

        let notificationObject = {
            file: props.file.key,
            title: props.file.displayName,
            type: fileType,
            users: jsonFromArray(props.selectedUsers),
            managers: managersObject,
            message: message,
            date: dayjs().toString(),
            fromDate: fromDate.toString(),
        };

        if (props.file.url !== undefined) {
            notificationObject.url = props.file.url;
        }

        if (props.file.thumbnail !== undefined) {
            notificationObject.thumbnail = props.file.thumbnail;
        }

        if (props.ancestorHubs && props.ancestorHubs.length > 0) {
            let nearestHub = [...props.ancestorHubs].reverse()[0];
            if (nearestHub !== undefined) {
                notificationObject.hubName = nearestHub.name;
            }
        }

        return notificationsRef.push(notificationObject);
    }

    function sendFileEmails() {
        const excludeManagers = false;

        var link = "https://nodiant.faw.cymru/";

        if (props.file.type.includes("video")) {
            link += "video/";
        } else if (props.file.type === "application/pdf") {
            link += "pdf/";
        } else if (props.file.type.includes("audio")) {
            link += "audio/";
        } else if (props.file.type.includes("image")) {
            link += "image/";
        } else {
            link += "file/";
        }

        link += props.file.key;

        let allRecipients = [...props.selectedUsers];

        if (!excludeManagers) {
            allRecipients = [...allRecipients, ...props.managers];
        }

        // selectedUsers and managers overlap, and concatenating them used to
        // email anyone in both lists twice. recipientCount below has always
        // deduped; the send now agrees with it.
        const emailUsers = Array.from(
            new Map(
                allRecipients
                    .filter((user) => user && user.key)
                    .map((user) => [user.key, user]),
            ).values(),
        );

        if (emailUsers.length === 0) return Promise.resolve();

        const messageSection = message
            ? `<tr>
                <td style="padding: 0 30px 20px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #555555;">
                    ${message.replace(/\n/g, "<br>")}
                </td>
               </tr>`
            : "";

        const emailHtml = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: Arial, Helvetica, sans-serif;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4;">
        <tr>
            <td align="center" style="padding: 20px 0;">
                <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                    <!-- Green Header Bar -->
                    <tr>
                        <td style="background-color: rgb(0, 149, 61); padding: 20px 30px;">
                            <h1 style="margin: 0; font-family: Arial, Helvetica, sans-serif; font-size: 22px; color: #ffffff;">Nodiant Online</h1>
                        </td>
                    </tr>
                    <!-- Content -->
                    <tr>
                        <td style="padding: 30px 30px 10px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #333333;">
                            A new file has been shared with you:
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 0 30px 20px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 18px; font-weight: bold; color: #333333;">
                            ${props.file.displayName}
                        </td>
                    </tr>
                    ${messageSection}
                    <!-- Button -->
                    <tr>
                        <td style="padding: 0 30px 30px 30px;">
                            <a href="${link}" style="display: inline-block; padding: 12px 24px; background-color: rgb(0, 149, 61); color: #ffffff; text-decoration: none; border-radius: 4px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; font-weight: bold;">View File</a>
                        </td>
                    </tr>
                    <!-- White Footer Bar with Logo -->
                    <tr>
                        <td style="background-color: #ffffff; border-top: 1px solid #e0e0e0; padding: 15px 30px; text-align: center;">
                            <img src="https://firebasestorage.googleapis.com/v0/b/nodiant-e20a8.appspot.com/o/FAW_CLEAR.png?alt=media&token=a5b922df-5982-4fd9-af85-5609bb390d55" alt="FAW Logo" height="40" style="height: 40px;">
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;

        // A future fromDate always embargoes here, so the flag is constant.
        return sendSharedItemEmails({
            embargoNotifications: true,
            firebase,
            html: emailHtml,
            label: `file: ${props.file.displayName}`,
            notificationDate: fromDate,
            recipients: emailUsers,
            subject: "Nodiant Online - New File Available",
        });
    }

    const recipientCount = new Set(
        [...(props.selectedUsers || []), ...(props.managers || [])]
            .map((user) => user.key)
            .filter(Boolean),
    ).size;

    return (
        <>
        <Dialog
            onClose={handleClose}
            aria-labelledby="file-notifications-dialog-title"
            open={props.open}
            fullWidth
            maxWidth="sm"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: "92vh",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <NotificationsOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="file-notifications-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Send notifications
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {props.file?.displayName}
                    </Typography>
                </Box>
                <Chip
                    label={`${recipientCount} ${
                        recipientCount === 1 ? "recipient" : "recipients"
                    }`}
                    size="small"
                    sx={{ display: { xs: "none", sm: "flex" } }}
                />
                <IconButton
                    aria-label="Close notifications"
                    onClick={handleClose}
                    disabled={isSending}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                >
                    Delivery
                </Typography>
                <Box
                    sx={{
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        overflow: "hidden",
                        backgroundColor: "background.paper",
                    }}
                >
                    <NotificationMethodRow
                        icon={<NotificationsOutlinedIcon />}
                        title="In-platform notification"
                        description="Show a notification when users open Nodiant."
                        checked={sendNotifications}
                        onChange={handleSwitchChange}
                        name="send"
                    />
                    <NotificationMethodRow
                        icon={<EmailOutlinedIcon />}
                        title="Email"
                        description="Send an email to eligible recipients."
                        checked={sendEmails}
                        onChange={handleSwitchChange}
                        name="email"
                        divided
                    />
                    <NotificationMethodRow
                        icon={<CalendarMonthOutlinedIcon />}
                        title="Embargo notifications"
                        description="Hold notifications until a selected date."
                        checked={embargoNotifications}
                        onChange={handleSwitchChange}
                        name="embargo"
                        divided
                    />
                </Box>

                <Box
                    sx={{
                        mt: 2,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 1.5, fontWeight: 800 }}
                    >
                        Message
                    </Typography>
                    <TextField
                        id="file-notification-message"
                        label="Optional message"
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={8}
                        value={message}
                        onChange={handleChangeMessageText}
                        fullWidth
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}
                    />
                </Box>

                {embargoNotifications && (
                    <Box
                        sx={{
                            mt: 2,
                            p: { xs: 2, sm: 2.5 },
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800 }}
                        >
                            Embargo date
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.25, mb: 1.5 }}
                        >
                            Select the date when the notifications should
                            become available.
                        </Typography>
                        <Box sx={notificationCalendarSx}>
                            <Calendar
                                value={fromDate}
                                onChange={onFromDateChange}
                                minDate={new Date()}
                                defaultView="month"
                                locale="en-GB"
                                prev2Label={null}
                                next2Label={null}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    gap: 1,
                }}
            >
                <Button
                    color="inherit"
                    onClick={handleClose}
                    disabled={isSending}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={<SendIcon />}
                    onClick={handleSendClick}
                    disabled={
                        isSending || (!sendNotifications && !sendEmails)
                    }
                >
                    {isSending ? "Sending…" : "Send"}
                </Button>
            </DialogActions>
            {isSending && <LinearProgress />}
        </Dialog>
        <SuccessDialog
            open={confirmation !== null}
            onClose={handleConfirmationClose}
            title={confirmation?.title}
            details={confirmation?.details || []}
        />
        </>
    );
}

const notificationCalendarSx = {
    "& .react-calendar": {
        width: "100%",
        border: "none",
        boxShadow: "none",
        color: "text.primary",
        backgroundColor: "transparent",
        fontFamily: "inherit",
        lineHeight: 1.25,
    },
    "& .react-calendar button": {
        border: "none",
        fontFamily: "inherit",
    },
    "& .react-calendar__navigation": {
        height: 42,
        marginBottom: 0.5,
        display: "flex",
        gap: 0.5,
    },
    "& .react-calendar__navigation button": {
        borderRadius: "9px !important",
        fontWeight: 800,
    },
    "& .react-calendar__navigation button:enabled:hover, & .react-calendar__navigation button:enabled:focus":
        {
            backgroundColor: "action.hover",
        },
    "& .react-calendar__navigation button[disabled], & .react-calendar__tile:disabled":
        {
            color: "text.disabled",
            backgroundColor: "action.disabledBackground",
        },
    "& .react-calendar__month-view__weekdays": {
        color: "text.secondary",
        fontSize: 11,
        fontWeight: 800,
    },
    "& .react-calendar__month-view__weekdays abbr": {
        textDecoration: "none",
    },
    "& .react-calendar__tile": {
        minHeight: 42,
        padding: "8px 3px",
        borderRadius: "9px !important",
        color: "text.primary",
    },
    "& .react-calendar__month-view__days__day--neighboringMonth": {
        color: "text.disabled",
    },
    "& .react-calendar__month-view__days__day--weekend": {
        color: "text.primary",
    },
    "& .react-calendar__tile:enabled:hover, & .react-calendar__tile:enabled:focus":
        {
            backgroundColor: "action.hover",
        },
    "& .react-calendar__tile--now": {
        color: "primary.main",
        backgroundColor: "action.hover",
        fontWeight: 800,
    },
    "& .react-calendar__tile--active, & .react-calendar__tile--active:enabled:hover, & .react-calendar__tile--active:enabled:focus":
        {
            color: "white",
            backgroundColor: "primary.dark",
        },
};
